import { FolderKanban, LayoutDashboard, Inbox, Mail, User, Search, Users } from 'lucide-react';
import { Header as HeaderPrimitive, useHeaderContext } from "./ui/header";
import { SidebarTrigger } from "./ui/sidebar";
import ThemeToggle from "@/components/ThemeToggle";
import NutMessageToggle from "@/components/NutMessageToggle";
import NutMessageOverlay from "@/components/NutMessageOverlay";
import { cn } from "@/lib/utils";
import { useLocation, Link } from "react-router-dom";
import { useState } from "react";
import ProjectIssuesSidebar from "./ProjectIssuesSidebar";

// Custom overflow toggle with test ID
function OverflowToggle() {
  const { overflowCount } = useHeaderContext();
  if (overflowCount <= 0) return null;

  return (
    <SidebarTrigger
      className="inline-flex items-center justify-center rounded-md border px-2.5 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label="Open overflow menu"
      data-testid="sidebar-toggle"
    >
      <span aria-hidden>⋯</span>
    </SidebarTrigger>
  );
}

// Navigation items are either links or sidebar content.
export const navItems: Array<{
  path: string;
  label: string;
  icon: React.ElementType | undefined;
  ariaLabel?: string;
} | { sidebarContent: React.ElementType }> = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { sidebarContent: ProjectIssuesSidebar },
  { path: '/triage', label: 'Triage', icon: Inbox },
  { path: '/inbox', label: 'Inbox', icon: Mail },
  { path: '/my-issues', label: 'My Issues', icon: User },
  { path: '/search', label: 'Search', icon: Search },
  { path: '/team', label: 'Team', icon: Users },
];

export function Header({ className }: { className?: string }) {
  const location = useLocation();
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // When there is content to render in the sidebar, we force it to always render.
  const hasSidebarContent = navItems.some((item) => "sidebarContent" in item);

  return (
    <>
      <HeaderPrimitive className={cn("sticky top-0 border-b bg-background", className)} hasSidebarContent={hasSidebarContent}>
      <HeaderPrimitive.Left>
        <OverflowToggle />
<Link to="/" className="font-semibold flex items-center gap-2 px-2 hover:opacity-80 transition-opacity" data-testid="header-logo-link">
            <FolderKanban className="h-5 w-5 text-primary" />
            <h1 className="text-base font-semibold text-foreground tracking-tight" data-testid="header-title">Project Hub</h1>
        </Link>
      </HeaderPrimitive.Left>
      <HeaderPrimitive.Center>
        {navItems.map((item, index) => {
          if ("sidebarContent" in item) {
            return (
              <HeaderPrimitive.SidebarContent key={index}>
                <item.sidebarContent />
              </HeaderPrimitive.SidebarContent>
            );
          }
          const Icon = item.icon;
          // Mark as active if the current path matches
          const isActive = location.pathname === item.path;
          return (
            <HeaderPrimitive.Link
              key={item.path}
              to={item.path}
              aria-label={item.ariaLabel || item.label}
              className={cn(
                "flex items-center gap-2.5 px-3 py-2 rounded-md transition-all text-sm font-medium",
                isActive
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              )}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/[\(\)]/g, '')}`}
              aria-current={isActive ? "page" : undefined}
            >
              {Icon && <Icon aria-hidden className="h-4 w-4" />}
              <span>{item.label}</span>
            </HeaderPrimitive.Link>
          );
        })}
      </HeaderPrimitive.Center>
      <HeaderPrimitive.Right>
        <NutMessageToggle onClick={() => setIsOverlayOpen(true)} />
        <ThemeToggle />
      </HeaderPrimitive.Right>
    </HeaderPrimitive>

    <NutMessageOverlay
      isOpen={isOverlayOpen}
      onClose={() => setIsOverlayOpen(false)}
    />
    </>
  );
}

export default Header;
